// src/components/DevMode/Developer.jsx

import React, { useState } from "react";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { getAccessToken } from "services/axiosInstance";

const DEVELOPER_EMAILS = [
  "sarath.srimusuru@ttec.com",
  "anuranjan.prasad@ttec.com",
  "hitesh.kavar@ttec.com",
  "hema.jenu@ttec.com",
  "lavanya.madapathi@ttec.com",
  "saurabh.talekar@ttec.com",
  "mohammedrizwan.memon@ttec.com",
];

const DEV_HOST = "app-astt-ui-dev.azurewebsites.net";

// Matches iconButtonStyle used in the new Header.jsx
const iconButtonStyle = {
  color: "#3299FF",
  position: "relative",
  "& svg": {
    fontSize: 28,
  },
  "&:hover": {
    transform: "scale(1.1)",
    backgroundColor: "rgba(50, 153, 255, 0.1)",
  },
  transition: "all 0.2s ease-in-out",
};

const DeveloperMode = () => {
  const currentEmail = localStorage.getItem("EmailId") ?? "";
  const isDevEnvironment =
    window.location.host === DEV_HOST ||
    window.location.host === "localhost:3000";
  const isDeveloper =
    isDevEnvironment &&
    DEVELOPER_EMAILS.map((e) => e.toLowerCase()).includes(
      currentEmail.toLowerCase(),
    );

  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  if (!isDeveloper) return null;

  const handleOpen = async () => {
    setOpen(true);
    setToken("");
    setError("");
    setCopied(false);
    setLoading(true);

    try {
      const bearerToken = await getAccessToken();
      setToken(bearerToken);
    } catch (err) {
      setError("Failed to retrieve token. Please ensure you are logged in.");
      console.error("[DevMode] Token retrieval failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setToken("");
    setError("");
    setCopied(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <>
      <Tooltip
        title={
          <Typography variant="body2" sx={{ fontSize: "1rem" }}>
            Developer Mode
          </Typography>
        }
        arrow
      >
        <IconButton onClick={handleOpen} sx={iconButtonStyle}>
          <DeveloperModeIcon />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: 480 } }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeveloperModeIcon sx={{ color: "#3299FF" }} />
            <Typography
              sx={{
                fontFamily: "Urbanist",
                fontWeight: "bold",
                fontSize: 16,
                color: "#3F5765",
              }}
            >
              Developer Mode
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            px: 3,
            py: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{ fontFamily: "Urbanist", fontSize: 13, color: "#666" }}
          >
            Here is the access token. Copy and paste it into Swagger UI's
            Authorize dialog.
          </Typography>

          {loading && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}
            >
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: 13, color: "#666" }}>
                Acquiring token...
              </Typography>
            </Box>
          )}

          {error && (
            <Typography sx={{ fontSize: 13, color: "#d32f2f", mt: 1 }}>
              {error}
            </Typography>
          )}

          {token && !loading && (
            <>
              <Typography
                sx={{
                  fontFamily: "Urbanist",
                  fontWeight: "bold",
                  fontSize: 13,
                  color: "#3F5765",
                }}
              >
                Bearer Token
              </Typography>

              {/* Token display box */}
              <Box
                sx={{
                  backgroundColor: "#f9f9f9",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 1.5,
                  wordBreak: "break-all",
                  fontSize: 11,
                  fontFamily: "monospace",
                  color: "#333",
                  maxHeight: 220,
                  overflowY: "auto",
                  userSelect: "all",
                }}
              >
                {token}
              </Box>

              {/* Copy button */}
              <Button
                variant="contained"
                size="small"
                startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                onClick={handleCopy}
                sx={{
                  alignSelf: "flex-start",
                  backgroundColor: copied ? "#4caf50" : "#3299FF",
                  "&:hover": {
                    backgroundColor: copied ? "#43a047" : "#1976d2",
                  },
                  fontFamily: "Urbanist",
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: 13,
                }}
              >
                {copied ? "Copied!" : "Copy Token"}
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default DeveloperMode;
